import React, { useEffect, useId, useRef, useState } from 'react';
import { reload, sendEmailVerification, type User } from 'firebase/auth';
import styled from 'styled-components';
import { verificationActionSettings } from './emailVerification';
import { useChangeEmail, type ChangeEmailError } from './useChangeEmail';
import { suggestEmail } from '../v4/emailSuggest';
import { gaEmailChangeStarted, type GaAccountSurface } from '../lib/gaPixel';

const Notice = styled.section<{ $hub?: boolean }>`
  padding: ${({ $hub }) => $hub ? '24px' : '16px'};
  margin: 8px 0 16px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 12px;
  background: ${({ theme }) => theme.color.cream};
  color: ${({ theme }) => theme.color.inkSoft};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  h3 { margin: 0 0 8px; font-size: 16px; font-weight: 600; }
  p { margin: 0 0 12px; }
  strong { font-weight: 500; }
  [role='status'] { margin: 12px 0 0; }
`;

const ResendButton = styled.button`
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  border-radius: 24px;
  background: ${({ theme }) => theme.color.clay};
  color: #fff;
  font: inherit;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.color.clayPressed}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.inkSoft}; outline-offset: 3px; }
  &:disabled { opacity: 0.6; cursor: wait; }
`;

const CheckButton = styled(ResendButton)`
  margin-top: 4px;
  background: transparent;
  color: ${({ theme }) => theme.color.inkSoft};
  font-weight: 400;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.color.neutralButton}; }
`;

/* The change-email entry point. Deliberately the LOWEST-weight action in the
   card: resending and re-checking are what most people need, and this is the
   answer to a narrower question ("but it never arrived"). Styling it as a link
   rather than a third button is what keeps it ranked below the two above it. */
const ChangeLink = styled(CheckButton)`
  margin-top: 2px;
  min-height: 36px;
  color: ${({ theme }) => theme.color.clay};
  font-size: 13.5px;
`;

/* The secondary action inside the change flow — filled rather than bare, so
   Cancel reads as a real button next to the primary and not as body copy. */
const QuietButton = styled(ResendButton)`
  margin-top: 4px;
  background: ${({ theme }) => theme.color.neutralButton};
  color: ${({ theme }) => theme.color.inkSoft};
  font-weight: 400;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.color.neutralButtonPressed}; }
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.inkSoft};
`;

const Field = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 10px;
  background: #fff;
  font: inherit;
  color: ${({ theme }) => theme.color.inkSoft};
  &:focus { outline: none; border-color: ${({ theme }) => theme.color.clay}; }
`;

/* The did-you-mean row, same contract as the one on the sign-in modal: it
   SUGGESTS and never autocorrects, it never blocks submit, and it renders only
   when suggestEmail returns non-null so it collapses instead of reserving a
   layout hole. This is the one field where a second typo would put her straight
   back where she started, which is why the hint is wired in here too. */
const SuggestRow = styled.p`
  margin: 8px 2px 12px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.mutedText};
  button {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: ${({ theme }) => theme.color.clay};
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
`;

const PasswordRow = styled.div`
  position: relative;
`;

const PasswordInput = styled(Field)`
  padding-right: 68px;
`;

const ShowHideBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  background: none;
  border: 0;
  padding: 6px 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.clay};
  cursor: pointer;
`;

const ErrorText = styled.p`
  margin: 10px 0 12px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.errorRed};
`;

/* The reassurance line in the sent state. Quieter than the sentence above it
   because it answers a worry rather than giving an instruction. */
const Reassurance = styled.p`
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.mutedText};
`;

const CurrentAddress = styled.s`
  text-decoration-thickness: 1px;
  color: ${({ theme }) => theme.color.mutedText};
`;

const CHANGE_ERROR_COPY: Record<ChangeEmailError, string> = {
  'invalid-email': 'That doesn’t look like an email address. Check it and try again.',
  'same-email': 'That’s the address you already have. Enter the one you meant to use.',
  'in-use': 'That address is already on another account. Try a different one.',
  'too-many': 'Too many requests. Wait a few minutes before trying again.',
  'wrong-password': 'That password didn’t match. Try again.',
  // No password to re-enter, so a password prompt would be a form she cannot
  // fill. Name the thing that will actually work instead.
  'no-password':
    'For your security, sign out and sign back in, then change your email.',
  unavailable: 'We couldn’t reach Firebase. Check your connection and try again.',
  unknown: 'Something went wrong. Please try again shortly.',
};

/**
 * Authenticated users can resend or refresh Firebase's email_verified state —
 * and, since THE-486, fix the address itself when it was mistyped.
 *
 * The change-email entry point lives here rather than on a surface of its own
 * because this component's render condition IS the feature's trigger: signed
 * in, not anonymous, has an email, not yet verified. It self-hides the moment
 * the address is confirmed, which is also what enforces the scope boundary —
 * changing a VERIFIED address is a different, higher-risk feature and must not
 * become reachable from here.
 */
export const EmailVerificationNotice: React.FC<{ user: User | null; hub?: boolean; analyticsSurface?: GaAccountSurface }> = ({ user, hub = false, analyticsSurface }) => {
  const [sentUntil, setSentUntil] = useState(0);
  const checking = useRef(false);
  const [verifiedUid, setVerifiedUid] = useState<string | null>(null);
  const [busy, setBusy] = useState<'send' | 'check' | null>(null);
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const change = useChangeEmail(user);
  const emailFieldId = useId();
  const passwordFieldId = useId();
  useEffect(() => {
    if (!hub || !user || user.isAnonymous || user.emailVerified) return;
    let active = true;
    const check = async () => {
      if (document.visibilityState === 'hidden' || checking.current) return;
      checking.current = true;
      try {
        await reload(user);
        if (active && user.emailVerified) {
          await user.getIdToken(true);
          if (active) setVerifiedUid(user.uid);
        }
      } catch { /* Manual refresh remains available after a transient failure. */ }
      finally { checking.current = false; }
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      active = false;
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [hub, user]);
  useEffect(() => {
    if (!sentUntil) return;
    const timer = window.setTimeout(() => setSentUntil(0), Math.max(0, sentUntil - Date.now()));
    return () => window.clearTimeout(timer);
  }, [sentUntil]);
  if (!user || user.isAnonymous || !user.email || user.emailVerified || verifiedUid === user.uid) return null;
  const run = async (action: 'send' | 'check') => {
    if (busy || (hub && action === 'send' && sentUntil > Date.now())) return;
    setBusy(action);
    setMessage('');
    try {
      if (action === 'send') {
        await sendEmailVerification(user, verificationActionSettings);
        if (hub) setSentUntil(Date.now() + 60_000);
        setMessage('Verification email sent. Check your inbox and spam folder.');
      } else {
        await reload(user);
        if (user.emailVerified) {
          await user.getIdToken(true);
          setVerifiedUid(user.uid);
        } else setMessage('Open the verification link in your email, then check again.');
      }
    } catch (error) {
      const code = (error as { code?: string })?.code;
      setMessage(code === 'auth/too-many-requests'
        ? 'Too many requests. Wait a few minutes before trying again.'
        : action === 'send'
          ? 'Couldn’t send the verification email. Please try again shortly.'
          : 'Couldn’t check verification. Please try again shortly.');
    } finally { setBusy(null); }
  };

  const openChangeForm = () => {
    setMessage('');
    setDraft('');
    setPassword('');
    setShowPassword(false);
    change.begin();
    if (analyticsSurface) gaEmailChangeStarted({ surface: analyticsSurface });
  };

  const closeChangeForm = () => {
    setDraft('');
    setPassword('');
    setShowPassword(false);
    change.cancel();
  };

  const errorCopy = change.error ? CHANGE_ERROR_COPY[change.error] : null;
  // Only ever a hint on what she has typed — never on the stored address, and
  // never applied for her.
  const suggestion = suggestEmail(draft);

  if (change.step === 'form' || change.step === 'reauth') {
    const reauthing = change.step === 'reauth';
    return <Notice $hub={hub} aria-label="Email verification">
      <h3>{reauthing ? 'Confirm it’s you' : 'Change your email'}</h3>
      {reauthing
        ? <p>For your security, re-enter your password to change your email.</p>
        : <p>Currently <CurrentAddress>{user.email}</CurrentAddress> — {hub ? 'never confirmed.' : 'never verified.'}</p>}
      <form onSubmit={(e) => {
        e.preventDefault();
        if (change.busy) return;
        if (reauthing) void change.confirmReauth(password);
        else void change.submit(draft);
      }}>
        {reauthing ? <>
          <FieldLabel htmlFor={passwordFieldId}>Password</FieldLabel>
          <PasswordRow>
            <PasswordInput
              id={passwordFieldId}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <ShowHideBtn type="button" aria-pressed={showPassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}>
              {showPassword ? 'Hide' : 'Show'}
            </ShowHideBtn>
          </PasswordRow>
        </> : <>
          <FieldLabel htmlFor={emailFieldId}>New email address</FieldLabel>
          <Field
            id={emailFieldId}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {suggestion ? <SuggestRow>
            Did you mean{' '}
            <button type="button" onClick={() => setDraft(suggestion)}>{suggestion}</button>?
          </SuggestRow> : null}
        </>}
        {errorCopy ? <ErrorText role="alert">{errorCopy}</ErrorText> : null}
        <ResendButton type="submit" disabled={change.busy}>
          {change.busy
            ? 'Sending…'
            : reauthing ? 'Confirm and send link' : hub ? 'Send link to new address' : 'Send verification to new address'}
        </ResendButton>
        <QuietButton type="button" disabled={change.busy} onClick={closeChangeForm}>
          Cancel
        </QuietButton>
      </form>
    </Notice>;
  }

  if (change.step === 'sent') {
    return <Notice $hub={hub} aria-label="Email verification">
      <h3>Check your new inbox</h3>
      <p role="status">
        We sent a confirmation link to <strong>{change.pendingEmail}</strong>. Your address
        changes when you open it.
      </p>
      {/* The security property, said plainly: verifyBeforeUpdateEmail commits
          nothing until that link is opened, so the old address still works and
          abandoning costs her nothing. */}
      <Reassurance>
        {hub ? <>Until then you can still log in with <strong>{user.email}</strong>, and your boards stay exactly as they are.</>
          : <>Until then you can still sign in with <strong>{user.email}</strong> and your board stays exactly as it is.</>}
      </Reassurance>
      <QuietButton type="button" onClick={() => {
        setDraft(change.pendingEmail ?? '');
        change.changeAgain();
      }}>
        Use a different address
      </QuietButton>
    </Notice>;
  }

  return <Notice $hub={hub} aria-label="Email verification">
    <h3>{hub ? 'Confirm your email' : 'Verify your email'}</h3>
    <p>{hub ? <>We sent a link to <strong>{user.email}</strong>. Open it to finish setting up your account.</> : <>Open the verification link sent to <strong>{user.email}</strong>.</>}</p>
    <ResendButton type="button" disabled={!!busy || (hub && !!sentUntil)} onClick={() => run('send')}>
      {busy === 'send' ? 'Sending…' : hub ? sentUntil ? 'Sent' : 'Resend email' : 'Resend verification email'}
    </ResendButton>
    <CheckButton type="button" disabled={!!busy} onClick={() => run('check')}>
      {busy === 'check' ? 'Checking…' : hub ? 'Already confirmed? Refresh' : 'Check verification'}
    </CheckButton>
    <ChangeLink type="button" disabled={!!busy} onClick={openChangeForm}>
      Wrong email? Change it
    </ChangeLink>
    {message ? <p role="status">{message}</p> : null}
  </Notice>;
};
